import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  ArrowLeft, Mail, Server, CheckCircle, XCircle, RefreshCw,
  Loader2, Send, Clock, AlertTriangle, Inbox, BarChart3,
  MousePointerClick, Eye, RotateCcw, Filter, TrendingUp
} from 'lucide-react';
import { toast, Toaster } from '../ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  sent: 'bg-green-100 text-green-700',
  delivered: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
  bounced: 'bg-red-100 text-red-700',
  queued: 'bg-amber-100 text-amber-700',
  retry_failed: 'bg-orange-100 text-orange-700',
};

const TEMPLATE_LABELS = {
  contract_send: 'Contract Send',
  contract_accepted: 'Contract Accepted',
  contract_accepted_admin: 'Contract Admin Notif',
  quote_send: 'Quote Send',
  lead_magnet_followup: 'Lead Magnet',
  appointment_confirmation: 'Appointment Confirm',
  appointment_admin_notification: 'Appointment Admin',
  admin_test: 'Test Email',
  admin_test_email: 'Test Email',
};

const EmailSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: logPage, per_page: 15 });
      if (statusFilter) params.set('status_filter', statusFilter);
      if (templateFilter) params.set('template_filter', templateFilter);

      const [sRes, aRes, lRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/email/settings`),
        fetch(`${BACKEND_URL}/api/admin/email/analytics`),
        fetch(`${BACKEND_URL}/api/admin/email/log?${params}`),
      ]);
      if (sRes.ok) setSettings(await sRes.json());
      if (aRes.ok) setAnalytics(await aRes.json());
      if (lRes.ok) {
        const d = await lRes.json();
        setLogs(d.logs || []);
        setLogTotal(d.total || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [logPage, statusFilter, templateFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendTestEmail = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/email/test`, { method: 'POST' });
      const data = await res.json();
      const result = data.result || {};
      if (result.status === 'sent') toast.success(`Test email sent via ${result.provider}`);
      else toast.error(`Failed: ${result.failure_reason || 'Unknown'}`);
      fetchAll();
    } catch { toast.error('Failed to send test email'); }
    setTesting(false);
  };

  const retryEmail = async (emailId) => {
    setRetrying(emailId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/email/retry/${emailId}`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Retry ${d.status}: attempt #${d.retry_count}`);
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Retry failed');
      }
      fetchAll();
    } catch { toast.error('Retry failed'); }
    setRetrying(null);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  const s = settings || {};
  const a = analytics || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="email-settings-page">
      <Toaster />
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Email Settings & Analytics</h1>
              <p className="text-sm text-gray-500">Provider config, delivery metrics, and engagement tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchAll} variant="outline" data-testid="refresh-email-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button onClick={sendTestEmail} disabled={testing}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-test-email-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test
            </Button>
          </div>
        </div>

        {/* Provider Status */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card data-testid="provider-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Server className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Provider</p>
                  <p className="text-sm font-bold text-gray-900 capitalize" data-testid="provider-name">{s.provider}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700 text-[10px]">
                  <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> active
                </Badge>
                {s.webhook_secret_configured && (
                  <Badge className="bg-purple-100 text-purple-700 text-[10px]">webhooks</Badge>
                )}
                {s.test_mode && <Badge className="bg-amber-100 text-amber-700 text-[10px]">TEST</Badge>}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="sender-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 uppercase">Sender</p>
                  <p className="text-xs font-bold text-gray-900 truncate" data-testid="sender-email">{s.sender_name}</p>
                  <p className="text-[10px] text-gray-400 truncate">{s.sender_email}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="total-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Inbox className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Total Emails</p>
                  <p className="text-lg font-bold text-gray-900" data-testid="total-emails">{a.total || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="delivery-rate-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Delivery Rate</p>
                  <p className="text-lg font-bold text-gray-900">{a.delivery_rate || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Grid */}
        <Card className="mb-6" data-testid="analytics-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-600" /> Delivery & Engagement Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
              {[
                { label: 'Sent', value: a.sent || 0, color: 'text-green-600', bg: 'bg-green-50' },
                { label: 'Delivered', value: a.delivered || 0, color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Opened', value: `${a.opened || 0} (${a.open_rate || 0}%)`, color: 'text-purple-600', bg: 'bg-purple-50' },
                { label: 'Clicked', value: `${a.clicked || 0} (${a.click_rate || 0}%)`, color: 'text-teal-600', bg: 'bg-teal-50' },
                { label: 'Failed', value: a.failed || 0, color: a.failed > 0 ? 'text-red-600' : 'text-gray-400', bg: a.failed > 0 ? 'bg-red-50' : 'bg-gray-50' },
                { label: 'Bounced', value: `${a.bounced || 0} (${a.bounce_rate || 0}%)`, color: a.bounced > 0 ? 'text-red-600' : 'text-gray-400', bg: 'bg-gray-50' },
                { label: 'Retried', value: a.retried || 0, color: 'text-amber-600', bg: 'bg-amber-50' },
              ].map((m, i) => (
                <div key={m.label || `metric-${i}`} className={`text-center p-3 ${m.bg} rounded-lg`} data-testid={`metric-${m.label.toLowerCase()}`}>
                  <p className={`text-lg font-bold ${m.color}`}>{m.value}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">{m.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Template Performance */}
        {(a.templates || []).length > 0 && (
          <Card className="mb-6" data-testid="template-performance-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <MousePointerClick className="w-4 h-4 text-blue-600" /> Template Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(a.templates || []).map((t, i) => (
                  <div key={t.name || `template-${i}`} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg" data-testid={`template-${i}`}>
                    <span className="text-xs font-medium text-gray-700 w-40 truncate">
                      {TEMPLATE_LABELS[t.template] || t.template}
                    </span>
                    <div className="flex-1 flex items-center gap-4 text-[10px]">
                      <span className="text-green-600">{t.sent} sent</span>
                      {t.failed > 0 && <span className="text-red-600">{t.failed} failed</span>}
                      <span className="text-purple-600">{t.opened} opened ({t.open_rate}%)</span>
                      <span className="text-teal-600">{t.clicked} clicked</span>
                    </div>
                    <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${Math.min(t.count / Math.max(a.total, 1) * 100, 100)}%` }} />
                    </div>
                    <span className="text-xs font-bold text-gray-600 w-8 text-right">{t.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Email Delivery Log */}
        <Card data-testid="email-log-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" /> Delivery Log
              </CardTitle>
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-gray-400" />
                <select className="text-xs border rounded px-2 py-1" value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setLogPage(1); }} data-testid="status-filter">
                  <option value="">All Status</option>
                  <option value="sent">Sent</option>
                  <option value="delivered">Delivered</option>
                  <option value="failed">Failed</option>
                  <option value="bounced">Bounced</option>
                  <option value="retry_failed">Retry Failed</option>
                </select>
                <select className="text-xs border rounded px-2 py-1" value={templateFilter}
                  onChange={e => { setTemplateFilter(e.target.value); setLogPage(1); }} data-testid="template-filter">
                  <option value="">All Templates</option>
                  <option value="contract_send">Contract Send</option>
                  <option value="contract_accepted">Contract Accepted</option>
                  <option value="quote_send">Quote Send</option>
                  <option value="lead_magnet_followup">Lead Magnet</option>
                  <option value="appointment_confirmation">Appointment</option>
                  <option value="admin_test">Test</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No emails match this filter.</p>
            ) : (
              <div className="space-y-2">
                {logs.map((log, i) => (
                  <div key={log.email_id || i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`email-log-${i}`}>
                    <Badge className={`text-[10px] px-2 flex-shrink-0 ${STATUS_BADGE[log.status] || 'bg-gray-100 text-gray-600'}`}>
                      {log.status}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{log.subject}</p>
                      <div className="flex items-center gap-3 text-[10px] text-gray-400 mt-0.5">
                        <span>To: {log.recipient}</span>
                        <span>{log.provider}</span>
                        <span>{TEMPLATE_LABELS[log.template] || log.template || '—'}</span>
                        {log.open_count > 0 && (
                          <span className="text-purple-500 flex items-center gap-0.5">
                            <Eye className="w-2.5 h-2.5" /> {log.open_count}
                          </span>
                        )}
                        {log.click_count > 0 && (
                          <span className="text-teal-500 flex items-center gap-0.5">
                            <MousePointerClick className="w-2.5 h-2.5" /> {log.click_count}
                          </span>
                        )}
                        {log.retry_count > 0 && (
                          <span className="text-amber-500">retry:{log.retry_count}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 flex items-center gap-2">
                      <p className="text-[10px] text-gray-400">
                        {log.sent_at ? new Date(log.sent_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                      </p>
                      {(log.status === 'failed' || log.status === 'retry_failed') && (
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-amber-600 hover:bg-amber-50"
                          disabled={retrying === log.email_id || (log.retry_count || 0) >= 3}
                          onClick={() => retryEmail(log.email_id)} data-testid={`retry-${log.email_id}`}>
                          {retrying === log.email_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <><RotateCcw className="w-3 h-3 mr-0.5" /> Retry</>
                          )}
                        </Button>
                      )}
                    </div>
                    {log.failure_reason && (
                      <div className="text-[10px] text-red-500 flex items-center gap-1 flex-shrink-0">
                        <AlertTriangle className="w-2.5 h-2.5" /> {log.failure_reason.substring(0, 35)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {logTotal > 15 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button size="sm" variant="outline" disabled={logPage <= 1} onClick={() => setLogPage(p => p - 1)}>Prev</Button>
                <span className="text-xs text-gray-500">Page {logPage} of {Math.ceil(logTotal / 15)}</span>
                <Button size="sm" variant="outline" disabled={logPage >= Math.ceil(logTotal / 15)} onClick={() => setLogPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailSettings;
